import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { usePaymentPageToken } from 'api/usePaymentPageToken';
import { Button } from 'components/Elements/Buttons/Button';
import { PaymentItem, PhysicianSearchGrid } from 'components/Modules/DataDisplay/PhysicianSearchGrid';
import { calculateTotal, formatToUSD } from 'utils/participation';

const subtitleSx = {
  fontWeight: 700,
  color: 'primary.main',
  fontSize: { xs: 20, md: 22 },
} as const;

const buttonFontSx = {
  fontWeight: 700,
} as const;

export interface ConfirmPaymentDialogProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (token: string) => void;
  items: PaymentItem[];
}

export const ConfirmPaymentDialog: React.FC<ConfirmPaymentDialogProps> = ({ open, handleClose, handleConfirm, items }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { data } = usePaymentPageToken({
    items: items.map(x => ({
      name: x.firstName + ' ' + x.lastName,
      amount: +x.amount,
      licenseNumber: x.license,
    })),
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle id="alert-dialog-title" variant="h4" sx={subtitleSx}>
        Confirm Provider(s) and Payment Amount
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" sx={{ mb: 2, color: 'text.primary' }}>
          Verify the following information and payment amount is correct before proceeding with your payment. Return to the previous screen
          to make corrections if needed. Please do not pay if you are exempt: see{' '}
          <Link href="https://vabirthinjury.com/providers/exempt-providers/" target="_blank" rel="noopener noreferrer" color="primary">
            Exempt Providers
          </Link>
          .
        </DialogContentText>
        <Typography mb={2}>
          Please verify name and license number before you proceed as there may be other physicians that share the same name.
        </Typography>
        <Box sx={{ width: '100%' }}>
          <PhysicianSearchGrid rows={items} variant="confirm" sx={{ width: '100%' }} />
          <Typography align="right" variant="h4" component="p" mt={2} sx={subtitleSx}>
            Total Payment Amount: {formatToUSD(calculateTotal(items))}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          size="medium"
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{
            ...buttonFontSx,
            bgcolor: '#FFFFFF',
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': {
              bgcolor: '#FFFFFF',
              borderColor: 'primary.main',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={() => handleConfirm(data?.token ?? '')}
          disabled={!data?.token}
          sx={{
            ...buttonFontSx,
            bgcolor: 'primary.main',
            color: '#FFFFFF',
            border: '1px solid',
            borderColor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.main',
              borderColor: 'primary.main',
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
